import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router";
import {
  useNotifications,
  useToggleState,
  notification_color_map,
  notification_variant_map,
} from "utils/hooks";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import constants from "constants/constants";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import {
  BANK_DETAILS_STRINGS,
  KYC_STATUS,
  GST_DETAIL_FIELDS,
  DEFAULT_BANNER,
  KYC_DEADLINE_CONST,
} from "../../constants";
import { useBankDetails } from "../../BankDetails.helpers";
import { isRequestSuccessful, pluralise } from "utils/Utils";
import { checkIsObjectEmpty } from "features/Common/modules/DataTypes/utils/object";

export const usePayoutLogic = () => {
  const { pushNotification } = useNotifications();
  const history = useHistory();
  const postSave = useRef(null);

  const [payout_data, set_payout_data] = useState({});
  const [gst, set_gst] = useState(false);
  const [gst_status, set_gst_status] = useState(constants.GST_STATUS.INCLUSIVE);
  const [getGSTInfo, setGetGSTInfo] = useState(false);
  const [isBillingStateMandatory, setIsBillingStateMandatory] = useState(false);
  const [access, set_access] = useState(false);
  const [taxes_terms, set_taxes_terms] = useState(true);
  const [isEdited, setIsEdited] = useState(false);
  const [saveComplete, setSaveComplete] = useState(false);
  const [proccessing, setProccessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [is_modal_active, show_modal, hide_modal] = useToggleState(false);
  const [isPanVerified, setIsPanVerified] = useState(false);
  const [panError, setPanError] = useState(null);

  const [showGSTModal, setShowGSTModal] = useState(false);
  const [linkedGSTAccounts, setLinkedGSTAccounts] = useState([]);
  const [selectedGSTAccount, setSelectedGSTAccount] = useState(null);

  const [isBankSaving, setIsBankSaving] = useState(false);
  const [isPanSaving, setIsPanSaving] = useState(false);
  const [isGstSaving, setIsGstSaving] = useState(false);
  const [isKycProcessing, setIsKycProcessing] = useState(false);

  const [openSections, setOpenSections] = useState({
    bank: true,
    pan: false,
    gst: false,
    kyc: false,
  });

  // Section completion states
  const [sectionCompletion, setSectionCompletion] = useState({
    bankDetails: false,
    panDetails: false,
    gstDetails: false,
    kycVerify: false,
  });

  const { loadUserBankDetails, handeSaveHelper } = useBankDetails({
    set_payout_data: (account_details) => {
      set_payout_data(account_details);
      setIsPanVerified(account_details?.is_pan_verified === true);

      if (!checkIsArrayEmpty(account_details.account_number)) {
        set_access(true);
      }

      const isGstRegistered =
        account_details.is_creator_gst_registered === true;
      set_gst(isGstRegistered);

      if (isGstRegistered) {
        const apiStatus = account_details.gst_status;
        const isValidStatus = apiStatus !== constants.GST_STATUS.INAPPLICABLE;
        set_gst_status(
          isValidStatus ? apiStatus : constants.GST_STATUS.INCLUSIVE
        );
      } else {
        set_gst_status(constants.GST_STATUS.INAPPLICABLE);
      }

      setGetGSTInfo(account_details.get_gst_info ?? false);
      setIsBillingStateMandatory(!account_details.billing_state_non_mandatory);
      window.localStorage.setItem(
        GST_DETAIL_FIELDS.gstStatus,
        account_details.gst_status
      );

      // Update section completion states based on existing data
      const isBankVerified = account_details?.is_bank_account_verified === true;

      const isPanVerified = account_details?.is_pan_verified === true;

      const isKycCompleted =
        account_details?.kyc_status === KYC_STATUS.kycStatus.COMPLETED;

      const isKycRequired = account_details?.is_kyc_required === true;

      const isGstVerified = account_details?.gst_info?.is_gst_verified === true;

      setSectionCompletion({
        bankDetails: isBankVerified,

        panDetails: isPanVerified,

        gstDetails: isGstVerified,

        // If backend says KYC required again,
        // force section to incomplete
        kycVerify: isKycRequired ? false : isKycCompleted,
      });
    },
    set_access: set_access,
    set_gst: () => {},
    set_gst_status: () => {},
    setGetGSTInfo: () => {},
    setIsBillingStateMandatory: () => {},
  });

  useEffect(() => {
    loadUserBankDetails();
  }, []);

  const handleCommonSuccess = () => {
    setProccessing(false);

    setIsBankSaving(false);
    setIsPanSaving(false);
    setIsGstSaving(false);
    setIsKycProcessing(false);

    setIsEdited(false);

    if (postSave.current) {
      postSave.current();
      postSave.current = null;
    }
  };

  const handleCommonError = (err) => {
    const errorMessage =
      err?.response?.data?.message ||
      err?.body?.message ||
      err?.message ||
      SOMETHING_WENT_WRONG;
    pushNotification(errorMessage, {
      variant: notification_variant_map.outlined,
      color: notification_color_map.error,
    });

    setProccessing(false);
    setIsBankSaving(false);
    setIsPanSaving(false);
    setIsGstSaving(false);
    setIsKycProcessing(false);
  };

  const handleBankDetailsSave = async (values) => {
    if (!access) {
      return pushNotification(
        BANK_DETAILS_STRINGS.NOTIFICATIONS.LEGAL_CHECKBOX,
        {
          variant: notification_variant_map.outlined,
          color: notification_color_map.warning,
        }
      );
    }

    // Validate form before saving
    const errors = validateBankDetails(values);

    if (!checkIsObjectEmpty(errors)) {
      pushNotification("Please fill all required fields correctly", {
        variant: notification_variant_map.outlined,
        color: notification_color_map.error,
      });
      return;
    }

    setProccessing(true);
    setIsBankSaving(true);

    try {
      const payload = {
        banking_details: {
          full_name: values.full_name?.trim(),
          account_number: values.account_number,
          account_type: values.account_type,
          bank_name: values.bank_name?.trim(),
          ifsc_code: values.ifsc_code?.trim()?.toUpperCase(),
          billing_state: values.billing_state,
        },
      };

      const status = await handeSaveHelper(payload);

      if (status) {
        setSectionCompletion((prev) => ({
          ...prev,
          bankDetails: true,
        }));

        handleCommonSuccess();

        pushNotification(status.message || "Bank details saved successfully", {
          variant: notification_variant_map.outlined,
          color: notification_color_map.success,
        });
      }
    } catch (err) {
      handleCommonError(err);
    }
  };

  const handleGSTDetailsSave = async (values, skipValidation = false) => {
    // Validate form before saving
    if (!skipValidation) {
      const errors = validateGSTDetails(values);

      if (!checkIsObjectEmpty(errors)) {
        pushNotification("Please fill all required GST fields correctly", {
          variant: notification_variant_map.outlined,
          color: notification_color_map.error,
        });

        return;
      }
    }

    setProccessing(true);
    setIsGstSaving(true);

    try {
      const payload = {
        gst_info: gst
          ? {
              gst_number: values.gst_info?.gst_number?.trim(),
              gst_name: values.gst_info?.gst_name?.trim(),
              gst_company_address: values.gst_info?.gst_company_address?.trim(),
              gst_city: values.gst_info?.gst_city?.trim(),
              pincode: values.gst_info?.pincode,

              is_creator_gst_registered: gst,
            }
          : {},
      };

      const status = await handeGstSaveHelper(payload);

      if (isRequestSuccessful(status?.status)) {
        if (
          status.data.is_gst_verified === true &&
          status.data.is_gst_linked_with_pan === true
        ) {
          setSectionCompletion((prev) => ({
            ...prev,
            gstDetails: true,
          }));

          handleCommonSuccess();

          pushNotification(status.message || "GST details saved successfully", {
            variant: notification_variant_map.outlined,
            color: notification_color_map.success,
          });
        } else if (
          status.data.is_gst_verified === false &&
          status.data.is_gst_linked_with_pan === false
        ) {
          setProccessing(false);
          setIsGstSaving(false);

          // Fetch linked GST accounts using the API
          const gstAccountsResponse = await handeGstListHelper();
          const gstAccounts = gstAccountsResponse?.linked_gst_accounts || [];

          pushNotification(
            status.message || "Could not be verified , Please try again later",
            {
              variant: notification_variant_map.outlined,
              color: notification_color_map.error,
            }
          );

          // OPEN GST MODAL IF GST ACCOUNTS EXIST
          if (!checkIsArrayEmpty(gstAccounts)) {
            setLinkedGSTAccounts(gstAccounts);
            setSelectedGSTAccount(gstAccounts?.[0]);
            setShowGSTModal(true);
          }
        } else {
          pushNotification(
            status?.message || "Could not be verified , Please try again later",
            {
              variant: notification_variant_map.outlined,
              color: notification_color_map.error,
            }
          );

          setProccessing(false);
          setIsGstSaving(false);
        }
      }
    } catch (err) {
      handleCommonError(err);
    }
  };

  const handleKYCVerification = async () => {
    setProccessing(true);
    setIsKycProcessing(true);

    try {
      const data = await handleKYCVerificationHelper();

      if (data?.contract_url) {
        // Open KYC flow
        window.open(data.contract_url, "_blank");

        pushNotification("KYC verification initiated successfully", {
          variant: notification_variant_map.outlined,
          color: notification_color_map.success,
        });
      } else {
        pushNotification(
          data?.message || "Failed to initiate KYC verification",
          {
            variant: notification_variant_map.outlined,
            color: notification_color_map.error,
          }
        );
      }
    } catch (err) {
      handleCommonError(err);
    } finally {
      setProccessing(false);
      setIsKycProcessing(false);
    }
  };

  const handlePANDetailsSave = async (values) => {
    // Validate form before saving
    const errors = validatePANDetails(values);

    if (!checkIsObjectEmpty(errors)) {
      pushNotification("Please fill all required PAN fields correctly", {
        variant: notification_variant_map.outlined,
        color: notification_color_map.error,
      });

      return;
    }

    setProccessing(true);
    setIsPanSaving(true);
    setPanError(null);

    try {
      const payload = {
        pan: values.pan_number?.trim()?.toUpperCase(),
        dob: values.pan_dob,
        pan_name: values.pan_full_name?.trim(),
      };

      const status = await handlePANSaveHelper(payload);

      if (status) {
        const isPanVerified = status?.is_pan_verified === true;

        setIsPanVerified(isPanVerified);

        if (!isPanVerified) {
          setPanError(true);
        }

        setSectionCompletion((prev) => ({
          ...prev,
          panDetails: isPanVerified,
        }));

        handleCommonSuccess();

        pushNotification(
          isPanVerified
            ? "PAN verified successfully"
            : "PAN details saved but verification is pending",
          {
            variant: notification_variant_map.outlined,
            color: isPanVerified
              ? notification_color_map.success
              : notification_color_map.warning,
          }
        );
      }
    } catch (err) {
      setPanError(true);
      handleCommonError(err);
    } finally {
      setProccessing(false);
      setIsPanSaving(false);
    }
  };

  const handleSave = async (values) => {
    delete values.user;
    setProccessing(true);

    if (!access) {
      setProccessing(false);
      return pushNotification(
        BANK_DETAILS_STRINGS.NOTIFICATIONS.LEGAL_CHECKBOX,
        {
          variant: notification_variant_map.outlined,
          color: notification_color_map.warning,
        }
      );
    }
    if (!taxes_terms) {
      setProccessing(false);
      return pushNotification(
        BANK_DETAILS_STRINGS.NOTIFICATIONS.TAXES_CHECKBOX,
        {
          variant: notification_variant_map.outlined,
          color: notification_color_map.warning,
        }
      );
    }

    const payload = {
      banking_details: {
        full_name: values.full_name?.trim(),
        account_number: values.account_number,
        account_type: values.account_type,
        bank_name: values.bank_name?.trim(),
        ifsc_code: values.ifsc_code?.trim()?.toUpperCase(),
        pan_number: values.pan_number?.trim()?.toUpperCase(),
        pan_full_name: values.pan_full_name?.trim(),
        pan_dob: values.pan_dob,
        billing_state: values.billing_state,
        get_gst_info: getGSTInfo,
        billing_state_non_mandatory: !isBillingStateMandatory,
        is_creator_gst_registered: gst,
        gst_status: gst ? gst_status : constants.GST_STATUS.INAPPLICABLE,
        gst_info: gst
          ? {
              gst_number: values.gst_info?.gst_number?.trim(),
              gst_name: values.gst_info?.gst_name?.trim(),
              gst_company_address: values.gst_info?.gst_company_address?.trim(),
              gst_city: values.gst_info?.gst_city?.trim(),
              pincode: values.gst_info?.pincode,
            }
          : {},
      },
    };

    try {
      const status = await handeSaveHelper(payload);
      if (isRequestSuccessful(status)) {
        setProccessing(false);
        setIsEdited(false);
        window.localStorage.setItem(GST_DETAIL_FIELDS.gstStatus, gst_status);

        // Update section completion states after successful save
        setSectionCompletion({
          bankDetails:
            !checkIsArrayEmpty(values.full_name) &&
            !checkIsArrayEmpty(values.account_number) &&
            !checkIsArrayEmpty(values.bank_name) &&
            !checkIsArrayEmpty(values.ifsc_code),
          panDetails:
            isPanVerified ||
            (!checkIsArrayEmpty(values.pan_number) &&
              !checkIsArrayEmpty(values.info?.pan_name) &&
              !checkIsArrayEmpty(values.pan_info?.dob)),
          gstDetails:
            gst &&
            !checkIsArrayEmpty(values.gst_info?.gst_number) &&
            !checkIsArrayEmpty(values.gst_info?.gst_name) &&
            !checkIsArrayEmpty(values.gst_info?.gst_company_address) &&
            !checkIsArrayEmpty(values.gst_info?.pincode),
        });

        if (checkIsArrayEmpty(payout_data.gst_info) && gst) {
          pushNotification(BANK_DETAILS_STRINGS.NOTIFICATIONS.GST_SUCCESS, {
            variant: notification_variant_map.outlined,
            color: notification_color_map.success,
          });
        }
        if (postSave.current) {
          postSave.current();
          postSave.current = null;
        }
        show_modal();
      }
    } catch (err) {
      pushNotification(err?.body?.message ?? SOMETHING_WENT_WRONG, {
        variant: notification_variant_map.outlined,
        color: notification_color_map.error,
      });
      setProccessing(false);
    }
  };

  const handleAlert = (values) => {
    if (!access) {
      return pushNotification(
        BANK_DETAILS_STRINGS.NOTIFICATIONS.LEGAL_CHECKBOX,
        {
          variant: notification_variant_map.outlined,
          color: notification_color_map.warning,
        }
      );
    }
    setShowAlert(true);
    setFormValues(values);
  };

  const panValidate = (values) => {
    const err = {};
    if (gst && checkIsArrayEmpty(values.pan_number)) {
      err.pan_number = "Required";
    }
    return err;
  };

  const validateBankDetails = (values) => {
    const errors = {};

    if (checkIsArrayEmpty(values.full_name)) {
      errors.full_name = "Required";
    }
    if (checkIsArrayEmpty(values.account_number)) {
      errors.account_number = "Required";
    }
    if (checkIsArrayEmpty(values.account_type)) {
      errors.account_type = "Required";
    }
    if (checkIsArrayEmpty(values.bank_name)) {
      errors.bank_name = "Required";
    }
    if (checkIsArrayEmpty(values.ifsc_code)) {
      errors.ifsc_code = "Required";
    }
    if (checkIsArrayEmpty(values.billing_state)) {
      errors.billing_state = "Required";
    }

    return errors;
  };

  const validatePANDetails = (values) => {
    const errors = {};

    if (gst && checkIsArrayEmpty(values.pan_number)) {
      errors.pan_number = "Required";
    }
    if (checkIsArrayEmpty(values.pan_full_name)) {
      errors.pan_full_name = "Required";
    }
    if (checkIsArrayEmpty(values.pan_dob)) {
      errors.pan_dob = "Required";
    }

    return errors;
  };

  const validateGSTDetails = (values) => {
    const errors = {};

    if (gst) {
      if (checkIsArrayEmpty(values.gst_info?.gst_name)) {
        errors.gst_name = "Required";
      }
      if (checkIsArrayEmpty(values.gst_info?.gst_number)) {
        errors.gst_number = "Required";
      }
      if (checkIsArrayEmpty(values.gst_info?.gst_company_address)) {
        errors.gst_company_address = "Required";
      }
      if (checkIsArrayEmpty(values.gst_info?.gst_city)) {
        errors.gst_city = "Required";
      }
      if (checkIsArrayEmpty(values.gst_info?.pincode)) {
        errors.pincode = "Required";
      }
    }

    return errors;
  };

  const getKycDeadlineText = (closingDate) => {
    if (!closingDate) return DEFAULT_BANNER.FallbackDay;

    const MS_IN_DAY = KYC_DEADLINE_CONST.MS_IN_DAY;

    const remainingDays = Math.max(
      KYC_DEADLINE_CONST.ZERO_DAYS,
      Math.ceil((new Date(closingDate).getTime() - Date.now()) / MS_IN_DAY)
    );

    if (remainingDays === KYC_DEADLINE_CONST.ZERO_DAYS)
      return DEFAULT_BANNER.today;

    return `${remainingDays} ${pluralise(DEFAULT_BANNER.day, remainingDays)}`;
  };

  const dayText = getKycDeadlineText(payout_data.kyc_closing_at);

  const isKYCExpired =
    payout_data?.kyc_closing_at != null &&
    !Number.isNaN(new Date(payout_data.kyc_closing_at).getTime()) &&
    new Date(payout_data.kyc_closing_at).getTime() < Date.now();

  const updateGstFields = async (fields) => {
    try {
      const payload = {
        banking_details: {
          gst_status,
          get_gst_info: getGSTInfo,
          billing_state_non_mandatory: !isBillingStateMandatory,
          ...fields,
        },
      };

      await handeSaveHelper(payload);

      pushNotification("GST Settings updated successfully", {
        variant: notification_variant_map.outlined,
        color: notification_color_map.success,
      });
    } catch (err) {
      handleCommonError(err);
    }
  };

  const handlePricingStructureChange = async () => {
    const newStatus =
      gst_status === constants.GST_STATUS.INCLUSIVE
        ? constants.GST_STATUS.EXCLUSIVE
        : constants.GST_STATUS.INCLUSIVE;

    set_gst_status(newStatus);

    await updateGstFields({
      gst_status: newStatus,
    });
  };

  const handleGetGstInfoToggle = async () => {
    const newValue = !getGSTInfo;

    setGetGSTInfo(newValue);

    await updateGstFields({
      get_gst_info: newValue,
    });
  };

  const handleBillingStateToggle = async () => {
    const newValue = !isBillingStateMandatory;

    setIsBillingStateMandatory(newValue);

    await updateGstFields({
      billing_state_non_mandatory: !newValue,
    });
  };

  const {
    handlePANSaveHelper,
    handleKYCVerificationHelper,
    handleKYCRefreshHelper,
    handeGstSaveHelper,
    handeGstListHelper,
  } = useBankDetails({
    set_payout_data: set_payout_data,
    set_access: set_access,
    set_gst: set_gst,
    set_gst_status: set_gst_status,
    setGetGSTInfo: setGetGSTInfo,
    setIsBillingStateMandatory: setIsBillingStateMandatory,
  });
  return {
    updateGstFields,
    handlePricingStructureChange,
    handleGetGstInfoToggle,
    handleBillingStateToggle,
    openSections,
    setOpenSections,
    payout_data,
    gst,
    set_gst,
    gst_status,
    set_gst_status,
    getGSTInfo,
    setGetGSTInfo,
    isBillingStateMandatory,
    setIsBillingStateMandatory,
    access,
    set_access,
    taxes_terms,
    set_taxes_terms,
    isEdited,
    setIsEdited,
    saveComplete,
    setSaveComplete,
    proccessing,
    isBankSaving,
    isPanSaving,
    isGstSaving,
    isKycProcessing,
    showAlert,
    setShowAlert,
    formValues,
    is_modal_active,
    show_modal,
    hide_modal,
    handleSave,
    handleBankDetailsSave,
    handleGSTDetailsSave,
    handlePANDetailsSave,
    handleAlert,
    panValidate,
    validateBankDetails,
    validatePANDetails,
    validateGSTDetails,
    history,
    postSave,
    sectionCompletion,
    showGSTModal,
    setShowGSTModal,
    linkedGSTAccounts,
    selectedGSTAccount,
    setSelectedGSTAccount,
    isPanVerified,
    panError,
    setPanError,
    handleKYCVerification,
    handleKYCRefreshHelper,
    setSectionCompletion,
    kycVerificationClosingDate: dayText,
    isKYCExpired,
  };
};
